import type {
  FulfillmentOrderDetail,
  ListOrdersParams,
  ListOrdersResponse,
} from '@/infrastructure/FulfillmentAdminClient';
import type { ArrivalSortReader } from './arrivalSortLookup';
import { MAX_BUNDLE_ORDERS } from './arrivalSortLookup';
import { kstDayOf, parcelNumbersOf } from './arrivalSortRules';

/** The reads the warm-up needs on top of the scan lookup: the order list it pages through. */
export interface ArrivalCacheReader extends ArrivalSortReader {
  listOrders(params: ListOrdersParams): Promise<ListOrdersResponse>;
}

/** Orders placed this many KST days back are the ones whose parcels are still arriving (rule 16). */
export const WARM_DAYS = 14;
/** Detail reads in flight at once while warming, so the desk does not flood the API on opening. */
export const WARM_CONCURRENCY = 6;
/** How often the page runs another warm-up round. */
export const WARM_REFRESH_MS = 10 * 60 * 1000;
/**
 * A cached order older than this is read again by the next round. Half the round interval, so every round
 * re-reads what the last one read (with the two equal, an order read at minute 0 was 9:59 old at minute 10
 * and skipped, and the whole cache was only re-read every 20 minutes); a live scan's fresh read still counts.
 */
export const WARM_STALE_MS = WARM_REFRESH_MS / 2;
const LIST_PAGE = 100;

const keyOf = (code: string) => code.trim().toUpperCase();

/**
 * The open orders the desk already read, indexed by every parcel number on their lines, so a scan of a
 * known parcel is judged from memory in a few milliseconds instead of three round trips (rule 16).
 */
export class ArrivalCache {
  private readonly details = new Map<number, { detail: FulfillmentOrderDetail; at: number }>();
  private readonly byCode = new Map<string, Set<number>>();
  private readonly codesOf = new Map<number, string[]>();

  public get size(): number {
    return this.details.size;
  }

  public put(detail: FulfillmentOrderDetail, at: number = Date.now()): void {
    const orderId = detail.header.orderId;
    for (const code of this.codesOf.get(orderId) ?? []) {
      this.byCode.get(code)?.delete(orderId);
    }
    const codes = [...new Set(detail.lines.flatMap((line) => parcelNumbersOf(detail, line).map(keyOf)))];
    for (const code of codes) {
      const orders = this.byCode.get(code) ?? new Set<number>();
      orders.add(orderId);
      this.byCode.set(code, orders);
    }
    this.codesOf.set(orderId, codes);
    this.details.set(orderId, { detail, at });
  }

  /** Drops orders no longer awaiting inbound, so a parcel of a shipped or cancelled order is asked of the server. */
  public keepOnly(orderIds: ReadonlySet<number>): number {
    let dropped = 0;
    for (const orderId of [...this.details.keys()]) {
      if (!orderIds.has(orderId)) {
        for (const code of this.codesOf.get(orderId) ?? []) {
          this.byCode.get(code)?.delete(orderId);
        }
        this.codesOf.delete(orderId);
        this.details.delete(orderId);
        dropped += 1;
      }
    }
    return dropped;
  }

  public has(orderId: number): boolean {
    return this.details.has(orderId);
  }

  public fetchedAt(orderId: number): number | null {
    return this.details.get(orderId)?.at ?? null;
  }

  /**
   * The bundle the code belongs to, read from memory and followed through shared parcels the same way the
   * live lookup does (rule 2). Null when the code is on no cached order: the desk then asks the server.
   */
  public bundle(code: string): { details: FulfillmentOrderDetail[]; truncated: boolean } | null {
    const first = this.byCode.get(keyOf(code));
    if (!first || first.size === 0) {
      return null;
    }
    const found = new Map<number, FulfillmentOrderDetail>();
    const queue = [...first];
    let truncated = false;
    while (queue.length > 0) {
      const orderId = queue.shift() as number;
      if (found.has(orderId)) {
        continue;
      }
      const entry = this.details.get(orderId);
      if (!entry) {
        continue;
      }
      if (found.size >= MAX_BUNDLE_ORDERS) {
        truncated = true;
        break;
      }
      found.set(orderId, entry.detail);
      for (const sibling of this.codesOf.get(orderId) ?? []) {
        for (const other of this.byCode.get(sibling) ?? []) {
          if (!found.has(other)) {
            queue.push(other);
          }
        }
      }
    }
    return { details: [...found.values()], truncated };
  }
}

export interface WarmProgress {
  listed: number;
  read: number;
  /** Order details that could not be read; those parcels are asked of the server when scanned. */
  failed: number;
  /** The whole open-order list arrived; false while it loads and when a page of it failed. */
  listComplete: boolean;
  done: boolean;
}

/** What the gauge shows once a round has finished (rule 16). */
export interface WarmLoaded {
  count: number;
  failed: number;
  at: string;
  /** The first good round loaded the whole list; until then the desk is not told it is ready. */
  complete: boolean;
  /** A later round whose list failed; the earlier load stays in use and the gauge says so. */
  reloadFailedAt: string | null;
}

/**
 * The gauge's state after a round ends. A later round whose list failed keeps the earlier load, which is
 * still in the cache, instead of replacing it with a failure; anything else replaces it.
 */
export const settleWarmRound = (previous: WarmLoaded | null, progress: WarmProgress, at: string): WarmLoaded | null => {
  if (!progress.done) {
    return previous;
  }
  if (!progress.listComplete && previous?.complete) {
    return { ...previous, reloadFailedAt: at };
  }
  return { count: progress.read, failed: progress.failed, at, complete: progress.listComplete, reloadFailedAt: null };
};

/**
 * Reads the open orders placed in the last WARM_DAYS KST days into the cache: the list first, page by
 * page, then each order's detail, a few at a time. Orders read within WARM_STALE_MS are skipped, so the
 * periodic round only re-reads what has aged. Failures are counted, never thrown: a cold cache only
 * means the desk asks the server, as it did before the cache existed.
 */
export const warmArrivalCache = async (
  reader: ArrivalCacheReader,
  cache: ArrivalCache,
  options: {
    nowIso: string;
    signal?: { aborted: boolean };
    onProgress?: (progress: WarmProgress) => void;
    days?: number;
    concurrency?: number;
  },
): Promise<WarmProgress> => {
  const days = options.days ?? WARM_DAYS;
  const today = kstDayOf(options.nowIso) ?? options.nowIso.slice(0, 10);
  const start = new Date(Date.parse(`${today}T00:00:00Z`) - days * 86_400_000).toISOString().slice(0, 10);
  const progress: WarmProgress = { listed: 0, read: 0, failed: 0, listComplete: false, done: false };
  // A round has begun: a desk already loaded shows "reloading" while the list comes in.
  options.onProgress?.({ ...progress });
  const ids: number[] = [];
  const params = (offset: number): ListOrdersParams => ({
    fulfillmentStatus: 'AWAITING_INBOUND',
    startDate: start,
    limit: LIST_PAGE,
    ...(offset > 0 && { offset }),
  });
  try {
    const first = await reader.listOrders(params(0));
    ids.push(...first.orders.map((order) => order.orderId));
    // A backend that predates the count sends none: then a full page means there may be more, and a short one ends it.
    const total = first.totalCount ?? Number.POSITIVE_INFINITY;
    let more = first.orders.length >= LIST_PAGE;
    for (let offset = LIST_PAGE; more && offset < total && !options.signal?.aborted; offset += LIST_PAGE) {
      const page = await reader.listOrders(params(offset));
      ids.push(...page.orders.map((order) => order.orderId));
      more = page.orders.length >= LIST_PAGE;
    }
    // Only a complete list may evict: an order missing from a list that failed half-way is not gone.
    progress.listComplete = !options.signal?.aborted;
    if (progress.listComplete) {
      cache.keepOnly(new Set(ids));
    }
  } catch {
    progress.listComplete = false;
  }
  progress.listed = ids.length;
  options.onProgress?.({ ...progress });

  const now = Date.now();
  const due = ids.filter((id) => {
    const at = cache.fetchedAt(id);
    return at == null || now - at > WARM_STALE_MS;
  });
  progress.read = ids.length - due.length;
  let next = 0;
  const worker = async () => {
    while (next < due.length && !options.signal?.aborted) {
      const orderId = due[next] as number;
      next += 1;
      const askedAt = Date.now();
      try {
        const detail = await reader.getOrderDetail(orderId);
        // A live scan may have read the order again while this request was out; keep the newer read. The
        // read is stamped with when it was asked, so it never passes for newer than a read asked after it.
        if ((cache.fetchedAt(orderId) ?? 0) <= askedAt) {
          cache.put(detail, askedAt);
        }
        progress.read += 1;
      } catch {
        progress.failed += 1;
      }
      // Every read moves the desk's gauge (rule 16).
      options.onProgress?.({ ...progress });
    }
  };
  await Promise.all(Array.from({ length: options.concurrency ?? WARM_CONCURRENCY }, worker));
  progress.done = !options.signal?.aborted;
  options.onProgress?.({ ...progress });
  return progress;
};
